import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftCircle, ChevronLeftIcon, ChevronRightCircle, ChevronRightIcon, MoreVertical } from "lucide-react";
import OnboardingCards from "./OnboardingCards";
import { useSidebar } from '../../SidebarContext';
import { decryptToken } from "../utils/getToken";

const Onboarding = () => {
  
  const [candidates, setCandidates] = useState([]);
  const [scheduleList, setScheduleList] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [currentTab, setCurrentTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [newCount, setNewCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [interviewTakenMap, setInterviewTakenMap] = useState({});
  const token = decryptToken("accessToken") || ""

  const navigate = useNavigate();
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const tabs = ["All", "New", "Scheduled"];
  const apiUrl = API_BASE_URL;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchAllData = () => {
    fetch(`${apiUrl}/career`, {
      headers: {
        "Authorization": `Bearer ${token}`
      }
    })
      .then((res) => res.json())
      .then((data) => {
        setCandidates(data);
        setNewCount(data.filter((c) => c.Status === "New").length);
      })
      .catch((err) => console.error("Failed to fetch candidates:", err));

    fetch(`${apiUrl}/schedule`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`
      }
    })
      .then((res) => res.json())
      .then((data) => setScheduleList(data))
      .catch((err) => console.error("Failed to fetch schedule:", err));
  };

  const fetchInterviewTaken = async () => {
    try {
      const res = await fetch(`${apiUrl}/question`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok) {
        const map = {};
        data.forEach(item => {
          const email = item.CandidateEmail?.toLowerCase();
          if (email && item.IsInterviewTaken) {
            map[email] = true;
          }
        });
        setInterviewTakenMap(map);
        console.log(data)
      }
    } catch (err) {
      console.error("Error fetching interview taken data", err);
    }
  };


  useEffect(() => {
    fetchAllData();
    fetchInterviewTaken(); // ✅ interview taken bhi load karo

  }, []);

  useEffect(() => {
    let filtered = [];

    if (currentTab === "All") filtered = [...candidates];
    else if (currentTab === "New") filtered = candidates.filter((c) => c.Status === "New");
    else if (currentTab === "Scheduled") {
      filtered = scheduleList
        .filter((s) => !interviewTakenMap[s.CandidateEmail?.toLowerCase()]) // remove if interview done
        .map((s) => {
          const careerMatch = candidates.find(
            (c) => c.CandidateEmail?.toLowerCase() === s.CandidateEmail?.toLowerCase()
          );
          return careerMatch ? { ...careerMatch, ...s, Status: "Schedule" } : null;
        })
        .filter(Boolean);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.CandidateName || "").toLowerCase().includes(q) ||
          (c.CandidateEmail || "").toLowerCase().includes(q) ||
          (c.Role || "").toLowerCase().includes(q)
      );
    }

    setFilteredCandidates(filtered);
    setCurrentPage(1);
  }, [currentTab, searchQuery, candidates, scheduleList, interviewTakenMap]); // ✅ added interviewTakenMap

  const getCountByStatus = (status) => {
    if (status === "All") return candidates.length;
    if (status === "New") return newCount;
    if (status === "Scheduled") {
      return scheduleList.filter(
        (s) => !interviewTakenMap[s.CandidateEmail?.toLowerCase()]
      ).length;
    }
    return 0;
  };

  const handleTabChange = async (tab) => {
    setCurrentTab(tab);
    if (tab === "New") {
      try {
        await fetch(`${apiUrl}/career/mark-seen`, {
          method: "PUT",
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });
      } catch (err) {
        console.error("Failed to mark seen:", err);
      }
    }
    fetchAllData();
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const currentData = filteredCandidates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const handlePageClick = (page) => setCurrentPage(page);

  const renderPageNumbers = () => {
    let pages = [];

    // Show first, last, current +/- 1
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageClick(i)}
            className={`px-3 py-1 rounded-md text-sm font-medium transition ${currentPage === i ? "bg-blue-600 text-white shadow" : "bg-gray-100 hover:bg-gray-200"
              }`}
          >
            {i}
          </button>
        );
      } else if (
        (i === currentPage - 2 && currentPage > 3) ||
        (i === currentPage + 2 && currentPage < totalPages - 2)
      ) {
        pages.push(<span key={`dots-${i}`} className="px-2 text-gray-500">...</span>);
      }
    }
    return pages;
  };

  return (
    <>
      <div className="space-y-6">
        {/* Tabs */}
        <div className="flex gap-3">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all shadow-sm ${currentTab === tab ? "bg-blue-600 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
            >
              {tab} <span className="ml-1 text-xs">({getCountByStatus(tab)})</span>
            </button>
          ))}
        </div>

        {/* Search + Kebab */}
        <div className="flex flex-row-reverse gap-4 items-center">
          <div className="flex justify-end relative" ref={menuRef}>
            <div
              className="p-2 rounded-full hover:bg-gray-100 cursor-pointer"
              onClick={() => setIsOpen((prev) => !prev)}
            >
              <MoreVertical className="w-5 h-5 text-gray-600" />
            </div>
            {isOpen && (
              <div className="absolute right-0 mt-2 bg-white shadow-lg rounded-lg w-40 z-50">
                <div
                  onClick={() => {
                    navigate("/career");
                    setIsOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                >
                  Add Candidate
                </div>
                {/* <div
                  onClick={() => setIsOpen(false)}
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                >
                  Export CSV
                </div> */}
              </div>
            )}
          </div>

          <div className="w-full">
            <input
              type="text"
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Onboarding Cards */}
        <OnboardingCards
          candidates={currentData}
          refreshCandidates={fetchAllData}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 pt-6 flex-wrap">
            <button
              onClick={handlePrevPage}
              className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300"
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon />
            </button>

            {renderPageNumbers()}

            <button
              onClick={handleNextPage}
              className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300"
              disabled={currentPage === totalPages}
            >
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Onboarding;
